import { useState } from 'react';
import { CollapsibleCard, type ToolRendererProps } from '../ToolCard';

const LONG_TEXT_THRESHOLD = 200;
const PRIORITY_KEYS = [
  'title',
  'name',
  'id',
  'status',
  'priority',
  'progress',
  'owner',
  'assignee',
  'description',
  'notes',
];

function isPrimitive(v: unknown): v is string | number | boolean {
  return typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';
}

function stringify(v: unknown): string {
  if (isPrimitive(v)) return String(v);
  if (v === null) return '—';
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

interface FieldProps {
  label: string;
  value: unknown;
}

function Field({ label, value }: FieldProps) {
  const [expanded, setExpanded] = useState(false);
  const str = stringify(value);
  const isLong = str.length > LONG_TEXT_THRESHOLD;
  const display = isLong && !expanded ? `${str.slice(0, LONG_TEXT_THRESHOLD)}…` : str;

  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wide text-bark/70">{label}</dt>
      <dd className="mt-0.5 whitespace-pre-wrap break-words text-xs text-espresso">
        {display}
        {isLong && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="ml-1 text-[10px] text-gold hover:underline"
          >
            {expanded ? 'less' : 'more'}
          </button>
        )}
      </dd>
    </div>
  );
}

export function RowanDetailCard({ toolName, result }: ToolRendererProps) {
  // Unwrap common envelopes ({task: {...}}, {project: {...}}, etc.)
  let obj: Record<string, unknown> | null = null;
  if (result && typeof result === 'object' && !Array.isArray(result)) {
    const candidate = result as Record<string, unknown>;
    for (const k of ['task', 'project', 'goal', 'kpi', 'observation', 'memory', 'decision', 'lesson']) {
      if (candidate[k] && typeof candidate[k] === 'object') {
        obj = candidate[k] as Record<string, unknown>;
        break;
      }
    }
    if (!obj) obj = candidate;
  }

  if (!obj) {
    return (
      <CollapsibleCard toolName={toolName}>
        <pre className="text-[11px] text-espresso">{JSON.stringify(result)}</pre>
      </CollapsibleCard>
    );
  }

  const keys = Object.keys(obj);
  const priority = PRIORITY_KEYS.filter((k) => k in obj!);
  const rest = keys.filter((k) => !priority.includes(k));
  const ordered = [...priority, ...rest];

  return (
    <CollapsibleCard toolName={toolName} defaultOpen>
      <dl className="space-y-1.5">
        {ordered.slice(0, 12).map((k) => (
          <Field key={k} label={k} value={obj![k]} />
        ))}
      </dl>
    </CollapsibleCard>
  );
}
